/**
 * №22.3 (D-CV11/D-CV12) — the anchor stream as a live SUBSCRIPTION.
 *
 * "A stream is a subscription, not a page" (D-CV11): while the panel is open, the
 * anchor's stream re-reads on an interval and on every collaboration mutation, so a
 * new run receipt / needs-you card / activity APPENDS into the conversation you're
 * already watching — no new window per event. Transport is an interval poll of the
 * projection (`get_anchor_stream`); a push transport (SSE/WS) is a drop-in behind
 * this same hook when the run model streams mid-generation (chip).
 *
 * The `["needs-you"]` / `["task-thread", …]` mutations already invalidate widely
 * (D-CC4); pairing that with the interval keeps the panel in lock-step with the rest
 * of the chrome.
 */
import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import type { AnchorStreamResult, StreamAnchorKind } from "../../lib/types";

export interface StreamAnchorRef {
  kind: StreamAnchorKind;
  id?: string;
  label?: string;
}

/** The live poll cadence while the panel is open (ms). Discrete events (run done, card
 *  raised, answer recorded) surface within this window; the answer choreography is
 *  immediate via mutation invalidation, so this is the ambient "everything else" beat. */
const LIVE_INTERVAL_MS = 2500;

export function useAnchorStream(anchor: StreamAnchorRef | null, opts?: { live?: boolean; limit?: number }) {
  const live = opts?.live ?? true;
  return useQuery<AnchorStreamResult>({
    queryKey: ["anchor-stream", anchor?.kind, anchor?.id ?? ""],
    queryFn: () => mcpClient.getAnchorStream(anchor!.kind, anchor?.id, opts?.limit),
    enabled: !!anchor,
    staleTime: 1_000,
    refetchInterval: live ? LIVE_INTERVAL_MS : false,
    refetchOnWindowFocus: true,
  });
}
